import React from 'react'
import { useSelector } from 'react-redux'
import useTrans from '@hooks/useTrans'
import { cn, dateFormat, getUserType } from '@utils/safeTs'
import { IndirizzoOrdineType, RootStateType, OrdineDetailType, UserType } from '@utils/types'
import { Checkmark } from '@components/icons'
import MyAccountOrdineDetailBox from '../MyAccountOrdineDetailBox/MyAccountOrdineDetailBox'
import styles from './MyAccountOrdineDetailInfoSpedizione.module.sass'
import { MyAccountTag, StatoOrdine } from '@components/atoms'
import _ from 'lodash'

export interface MyAccountOrdineDetailInfoSpedizioneProps {
  title?: string
  ordine?: OrdineDetailType
  bolle?: any
  userType?: UserType
  className?: string
}

const MyAccountOrdineDetailInfoSpedizione = ({ title, ordine, bolle = [], userType, className }: MyAccountOrdineDetailInfoSpedizioneProps) => {

  const t = useTrans()
  const { utente } = useSelector((state: RootStateType) => state?.utente)
  const serviceGift = useSelector((state: RootStateType) => state.servicegift.service_gift)

  const getOrdini = (ordine: OrdineDetailType) => ordine.isParent ? ordine.ordini : [ordine]
  const renderIndirizzo = (indirizzo?: IndirizzoOrdineType) => {
    
    if (!indirizzo)
      return undefined

    const lines = [
      indirizzo.ragioneSociale,
      indirizzo.indirizzo,
      [indirizzo.citta, indirizzo.cap].filter(x => !!x).join(", "),
      indirizzo.nazioneCodice,
    ].filter(x => !!x)

    if (!lines?.length) {
      return <span className={styles.placeholder}>—</span>
    }

    const [first, ...rest] = lines

    return (
      <p className={styles.address}>
        <strong>{first}</strong>
        {rest.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </p>
    )
  }

  const renderBoolean = (value: boolean) => (
    <span className={styles.boolean} aria-label={t(value ? "Sì" : "No")}>
      {value ? <Checkmark /> : '-'}
    </span>
  )

  const renderStatoOrdine = () => (
    <>
      <span><StatoOrdine stato={ordine.statoOrdineCalcolato} userType={userType} /></span>
    </>
  )

  const renderTassativo = (value: boolean) => (
    <span className={styles.boolean} aria-label={t(value ? "Sì" : "No")}>
      {value ? <MyAccountTag label={t("Tax")} variant='border' color="danger" /> : '-'}
    </span>
  )

  const renderBloccato = (value: boolean) => (
    <span className={styles.boolean} aria-label={t(value ? "Sì" : "No")}>
      {value ? <MyAccountTag label={t("B")} variant='border' color="dark-grey" /> : '-'}
    </span>
  )

  const data = userType === 'cliente' ? [
    { key: 'indirizzo_cliente', label: t("Cliente"), value: renderIndirizzo(getOrdini(ordine)?.[0]?.indirizzoFatturazione || getOrdini(ordine)?.[0]?.indirizzoAnagraficaAzienda) },
    { key: 'indirizzo_destinatario', label: t("Destinatario"), value: getOrdini(ordine)?.[0]?.codiceModalitaSpedizione === '002' ? renderIndirizzo(getOrdini(ordine)?.[0].indirizzo) : undefined },
    { key: 'stato', label: t("Stato ordine"), value: renderStatoOrdine(), align: 'center' },
    { key: 'created', label: t("Data creazione ordine"), value: dateFormat(ordine.created) },
    { key: 'partenza_prevista', label: t("Partenza prevista"), value: dateFormat(ordine.dataTermineLavorazione) },
    { key: 'evasione_ordine', label: t("Evasione ordine"), value: dateFormat(ordine.dataEvasione) }, 
    { key: 'numeroDocumento', label: t("Numero documento"), value: _.uniq(getOrdini(ordine).map(o => o.numeroDocumento).filter(r => !!r)).map((r, idx) => <>{idx > 0 ? <br /> : <></>}{r}</>) },
    { key: 'riferimento_lavorazione', label: t("Riferimento lavorazione"), value: getOrdini(ordine)?.[0].riferimento },
    { key: 'riferimento_cliente', label: t("Riferimento cliente"), value: _.uniq(getOrdini(ordine).map(o => o.riferimentoCliente).filter(r => !!r)).map((r, idx) => <>{idx > 0 ? <br /> : <></>}{r}</>) },
    ...serviceGift ? [
      { key: 'fattura_richiesta', label: t("Fattura richiesta"), value: t(getOrdini(ordine)?.[0].fatturaRichiesta ? "sì" : "no") }
    ] : []
  ] : [
    { key: 'indirizzo_cliente', label: t("Cliente"), value: renderIndirizzo(getOrdini(ordine)?.[0]?.indirizzoFatturazione || getOrdini(ordine)?.[0]?.indirizzoAnagraficaAzienda) },
    { key: 'indirizzo_destinatario', label: t("Destinatario"), value: getOrdini(ordine)?.[0]?.codiceModalitaSpedizione === '002' ? renderIndirizzo(getOrdini(ordine)?.[0].indirizzo) : undefined },
    { key: 'partenza_prevista', label: t("Partenza prevista"), value: ordine.dataTermineLavorazione ? ordine.dataTermineLavorazioneForzata ? <MyAccountTag label={dateFormat(ordine.dataTermineLavorazione)} variant="border" color='warning' /> : dateFormat(ordine.dataTermineLavorazione) : null },
    { key: 'evasione_ordine', label: t("Evasione ordine"), value: dateFormat(ordine.dataEvasione) }, 
    { key: 'tassativo', label: t("Tassativo"), value: renderTassativo(ordine.tassativo) }, 
    { key: 'bloccato', label: t("Stand-by"), value: renderBloccato(ordine.bloccato) },
    { key: 'prio', label: t("Prio"), value: renderBoolean(ordine.prio) },
    ...getOrdini(ordine).filter(o => o.ristampa)?.length ? [{ key: 'ristampa', label: t("Ristampa"), value: renderBoolean(true) }] : []
  ]
  
  return (
    <MyAccountOrdineDetailBox 
      title={t("Informazioni spedizione")} 
      className={cn(styles.root, className)}
      bodyClassName={styles.list}
    >
      {data.filter(item => typeof item.value !== 'undefined').map(item => (
        <div key={item.key} className={cn(styles.item, item.align ? styles[`item--${item.align}`] : '', styles[`item--${item.key}`])}>
          <div className={styles.itemLabel}>{item.label}:</div>
          <div className={styles.itemValue}>{item.value}</div>
        </div>
      ))}
    </MyAccountOrdineDetailBox>
  )
}

export default MyAccountOrdineDetailInfoSpedizione
